// Materio Global Style
import '../materio/globals.css';
// icon style
import '../icon/style.css';
// react-perfect-scrollbar Styles
import 'react-perfect-scrollbar/dist/css/styles.css';
import '@ice-web-app/shared-i18n-file';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { FC, ReactNode } from 'react';
import Providers from '../materio/components/Providers';

import { ComponentsOverrides } from '@mui/material';
import {
  CustomInputHorizontalProps,
  CustomInputImgProps,
  CustomInputVerticalProps,
} from '../materio/@core/components/custom-inputs/types';

declare module '@mui/material/styles' {
  // custom typography variants
  interface TypographyVariants {
    h3_600: React.CSSProperties;
    h4_600: React.CSSProperties;
    body1_500: React.CSSProperties;
    body2_500: React.CSSProperties;
    buttonLarge: React.CSSProperties;
    buttonMedium: React.CSSProperties;
    buttonSmall: React.CSSProperties;
    inputLabel: React.CSSProperties;
    helperText: React.CSSProperties;
    inputTextLarge: React.CSSProperties;
    inputText: React.CSSProperties;
    inputTextSmall: React.CSSProperties;
    avatarInitials: React.CSSProperties;
    chip: React.CSSProperties;
    tooltip: React.CSSProperties;
    alertText: React.CSSProperties;
    tableHeader: React.CSSProperties;
    badgeLabel: React.CSSProperties;
    navbar: React.CSSProperties;
    toast: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    h3_600?: React.CSSProperties;
    h4_600?: React.CSSProperties;
    body1_500?: React.CSSProperties;
    body2_500?: React.CSSProperties;
    buttonLarge?: React.CSSProperties;
    buttonMedium?: React.CSSProperties;
    buttonSmall?: React.CSSProperties;
    inputLabel?: React.CSSProperties;
    helperText?: React.CSSProperties;
    inputTextLarge?: React.CSSProperties;
    inputText?: React.CSSProperties;
    inputTextSmall?: React.CSSProperties;
    avatarInitials?: React.CSSProperties;
    chip?: React.CSSProperties;
    tooltip?: React.CSSProperties;
    alertText?: React.CSSProperties;
    tableHeader?: React.CSSProperties;
    badgeLabel?: React.CSSProperties;
    navbar?: React.CSSProperties;
    toast?: React.CSSProperties;
  }

  // Theme
  interface Theme {
    shape: {
      borderRadius: number;
      customBorderRadius: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
        xl: number;
      };
    };
    customShadows: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
      iceDefault: string;
    };
    mainColorChannels: {
      light: string;
      dark: string;
      lightShadow: string;
      darkShadow: string;
    };
  }
  interface ThemeOptions {
    shape?: {
      borderRadius?: number;
      customBorderRadius?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
      };
    };
    customShadows?: {
      xs?: string;
      sm?: string;
      md?: string;
      lg?: string;
      xl?: string;
      iceDefault?: string;
    };
    mainColorChannels?: {
      light?: string;
      dark?: string;
      lightShadow?: string;
      darkShadow?: string;
    };
  }

  // Palette Color
  interface PaletteColor {
    lighterOpacity?: string;
    lightOpacity?: string;
    mainOpacity?: string;
    darkOpacity?: string;
    darkerOpacity?: string;
    20: string;
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    // Opacity
    'opacity-8': string;
    'opacity-16': string;
    'opacity-24': string;
    'opacity-32': string;
    'opacity-38': string;
  }
  interface SimplePaletteColorOptions {
    lighterOpacity?: string;
    lightOpacity?: string;
    mainOpacity?: string;
    darkOpacity?: string;
    darkerOpacity?: string;
  }

  // Palette
  interface Palette {
    customColors: {
      bodyBg: string;
      chatBg: string;
      greyLightBg: string;
      inputBorder: string;
      tableHeaderBg: string;
      tooltipText: string;
      trackBg: string;
      goldBorder: string;
      greenBorder: string;
      purpleBorder: string;
    };
    iceGray: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      // Opacity
      'opacity-8': string;
      'opacity-16': string;
      'opacity-24': string;
      'opacity-32': string;
      'opacity-38': string;
    };
    turquoise: {
      main: string;
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      // Opacity
      'opacity-16'?: string;
    };
    primaryGray: {
      20: string;
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      // Opacity
      'opacity-8': string;
      'opacity-16': string;
      'opacity-24': string;
      'opacity-32': string;
      'opacity-38': string;
    };
  }
  interface PaletteOptions {
    customColors?: {
      bodyBg?: string;
      chatBg?: string;
      greyLightBg?: string;
      inputBorder?: string;
      tableHeaderBg?: string;
      tooltipText?: string;
      trackBg?: string;
    };
    iceGray?: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      // Opacity
      'opacity-8': string;
      'opacity-16': string;
      'opacity-24': string;
      'opacity-32': string;
      'opacity-38': string;
    };
    turquoise?: {
      main?: string;
      50?: string;
      100?: string;
      200?: string;
      300?: string;
      400?: string;
      500?: string;
      600?: string;
      700?: string;
      800?: string;
      900?: string;
      // Opacity
      'opacity-16'?: string;
    };
    primaryGray?: {
      20: string;
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      // Opacity
      'opacity-8': string;
      'opacity-16': string;
      'opacity-24': string;
      'opacity-32': string;
      'opacity-38': string;
    };
  }

  // Components
  interface ComponentNameToClassKey {
    MuiCustomInputHorizontal: 'root' | 'title' | 'meta' | 'content' | 'input';
    MuiCustomInputVertical: 'root' | 'title' | 'content' | 'input';
    MuiCustomImage: 'root' | 'image' | 'input';
  }

  interface ComponentsPropsList {
    MuiCustomInputHorizontal: CustomInputHorizontalProps;
    MuiCustomInputVertical: CustomInputVerticalProps;
    MuiCustomImage: CustomInputImgProps;
  }

  interface Components {
    MuiCustomInputHorizontal?: {
      defaultProps?: ComponentsPropsList['MuiCustomInputHorizontal'];
      styleOverrides?: ComponentsOverrides<Theme>['MuiCustomInputHorizontal'];
    };
    MuiCustomInputVertical?: {
      defaultProps?: ComponentsPropsList['MuiCustomInputVertical'];
      styleOverrides?: ComponentsOverrides<Theme>['MuiCustomInputVertical'];
    };
    MuiCustomImage?: {
      defaultProps?: ComponentsPropsList['MuiCustomImage'];
      styleOverrides?: ComponentsOverrides<Theme>['MuiCustomImage'];
    };
  }

  interface BreakpointOverrides {
    xxl: true;
    xxxl: true;
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    black: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsVariantOverrides {
    soft: true;
  }
}

declare module '@mui/material/Switch' {
  interface SwitchPropsSizeOverrides {
    large: true;
  }
}

declare module '@mui/material/Autocomplete' {
  interface AutocompletePropsSizeOverrides {
    large: true;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    large: true;
  }
}

declare module '@mui/material/InputBase' {
  interface InputBasePropsSizeOverrides {
    large: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    turquoise: true;
  }
  interface ChipPropsSizeOverrides {
    large: true;
  }
  interface ChipPropsVariantOverrides {
    tonal: true;
  }
}

declare module '@mui/material/Pagination' {
  interface PaginationPropsVariantOverrides {
    tonal: true;
  }
}

declare module '@mui/lab/TimelineDot' {
  interface TimelineDotPropsVariantOverrides {
    tonal: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h3_600: true;
    h4_600: true;
    body1_500: true;
    body2_500: true;
    button: false;
    buttonLarge: true;
    buttonMedium: true;
    buttonSmall: true;
    inputLabel: true;
    helperText: true;
    inputTextLarge: true;
    inputText: true;
    inputTextSmall: true;
    avatarInitials: true;
    chip: true;
    tooltip: true;
    alertText: true;
    tableHeader: true;
    badgeLabel: true;
    navbar: true;
    toast: true;
  }
}

interface Props {
  children: ReactNode;
  mode?: 'light' | 'dark';
  dir?: 'rtl' | 'ltr';
  bgColor?: string;
}

export const DesignSystemProvider: FC<Props> = ({ children, dir = 'ltr' }) => {
  return (
    <Providers direction={dir}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* <RTL themeDirection={dir}> */}
        {/* <CssBaseline>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link
            href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
            rel="stylesheet"
          />
        </CssBaseline> */}
        {children}
        {/* </RTL> */}
      </LocalizationProvider>
    </Providers>
  );
};
